import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import PieCardWithLegend, { objectToPieData } from '../charts/PieCardWithLegend';
import RequiredApiNotice from './RequiredApiNotice';

interface RealEstateDashboardProps {
  data: any;
  address: any;
}

function objectToChartData(obj: Record<string, number> | null): { name: string; value: number }[] {
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj)
    .map(([name, value]) => ({ name, value: Number(value) }))
    .filter((row) => row.name && !Number.isNaN(row.value));
}

function investorPieFromCensus(censusHousingData: {
  ownerOccupied?: number;
  renterOccupied?: number;
} | null | undefined) {
  if (censusHousingData?.ownerOccupied == null || censusHousingData?.renterOccupied == null) {
    return null;
  }
  const owner = Number(censusHousingData.ownerOccupied);
  const renter = Number(censusHousingData.renterOccupied);
  const total = owner + renter;
  if (!total) return null;
  return {
    'Investor owned': Math.round((renter / total) * 1000) / 10,
    'Homeowner owned': Math.round((owner / total) * 1000) / 10,
  };
}

const RealEstateDashboard: React.FC<RealEstateDashboardProps> = ({ data }) => {
  const BAR_HEIGHT = 200;

  if (!data || data.error) {
    return (
      <div>
        <div>Data not available</div>
        <RequiredApiNotice apis={['Rentcast AVM API', 'US Census ACS Housing API']} />
      </div>
    );
  }

  const medianHomeValue = data.medianHomeValue ?? data.censusHousingData?.medianHomeValue ?? null;
  const medianRent = data.medianRent ?? data.censusHousingData?.medianGrossRent ?? null;

  // Only show missing for core metrics; do not list availabilityNotes (Census often fills these)
  const missingFields: string[] = [];
  if (!medianHomeValue) missingFields.push('Median Home Value');
  if (!medianRent) missingFields.push('Median Rent');
  if (data.priceToRentRatio == null) missingFields.push('Price-to-Rent Ratio');

  const BAR_COLORS = ['#e65100', '#1e88e5', '#26a69a', '#fb8c00', '#8e24aa', '#43a047', '#ef5350'];

  const homeTypeLabels = ['Single Family', 'Condo/Townhome', 'Multi-Family', 'Duplex', 'Triplex', 'Quadplex', 'Other'];
  const toHomeTypeData = (pt: any) => {
    if (!pt) return [];
    const vals = [pt.singleFamily, pt.condo, pt.multiFamily, pt.duplex, pt.triplex, pt.quadplex, pt.other];
    return homeTypeLabels.map((name, i) => ({ name, value: Number(vals[i]) || 0 }));
  };
  const metroHomeTypeData = toHomeTypeData(data.metroPropertyTypes);
  const neighborhoodHomeTypeData = toHomeTypeData(data.propertyTypes);

  const neighborhoodBedroomData = objectToChartData(data.bedroomDistribution as Record<string, number>);
  const metroBedroomData = objectToChartData(data.metroBedroomDistribution as Record<string, number>);

  const neighborhoodRentData = objectToChartData(data.rentDistribution as Record<string, number>);
  const metroRentData = objectToChartData(data.metroRentDistribution as Record<string, number>);

  const neighborhoodValueData = objectToChartData(data.valueDistribution as Record<string, number>);
  const metroValueData = objectToChartData(data.metroValueDistribution as Record<string, number>);

  const neighborhoodHousingAgeData = objectToChartData(data.housingAgeDist as Record<string, number>);
  const metroHousingAgeData = objectToChartData(data.metroHousingAgeDist as Record<string, number>);

  const metroInvestorData = objectToPieData(
    (data.metroInvestorOwnershipDistribution ||
      investorPieFromCensus(data.metroCensusHousingData)) as Record<string, number>,
  );
  const neighborhoodInvestorData = objectToPieData(
    (data.investorOwnershipDistribution ||
      investorPieFromCensus(data.censusHousingData)) as Record<string, number>,
  );

  const neighborhoodMortgageData = objectToPieData(data.mortgageDistribution as Record<string, number>);
  const metroMortgageData = objectToPieData(data.metroMortgageDistribution as Record<string, number>);

  const renderBarCompare = (
    title: string,
    metroSeries: Array<{ name: string; value: number }>,
    neighborhoodSeries: Array<{ name: string; value: number }>,
    xAxisHeight = 60,
  ) => (
    <div style={{ marginBottom: 12 }}>
      <h3 style={{ marginBottom: 8 }}>{title}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="card" style={{ marginBottom: 0 }}>
          <h4 style={{ margin: '0 0 6px', fontWeight: 600 }}>Metro Area</h4>
          {metroSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={BAR_HEIGHT}>
              <BarChart data={metroSeries} margin={{ bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" angle={-28} textAnchor="end" height={xAxisHeight} tick={{ fontSize: 11 }} interval={0} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value">
                  {metroSeries.map((_, i) => (
                    <Cell key={`m-${i}`} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ color: '#6b7280', fontSize: 13 }}>Data not available.</p>
          )}
        </div>
        <div className="card" style={{ marginBottom: 0 }}>
          <h4 style={{ margin: '0 0 6px', fontWeight: 600 }}>Neighborhood</h4>
          {neighborhoodSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={BAR_HEIGHT}>
              <BarChart data={neighborhoodSeries} margin={{ bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" angle={-28} textAnchor="end" height={xAxisHeight} tick={{ fontSize: 11 }} interval={0} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value">
                  {neighborhoodSeries.map((_, i) => (
                    <Cell key={`n-${i}`} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ color: '#6b7280', fontSize: 13 }}>Data not available.</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderPieCompare = (
    title: string,
    metroSeries: Array<{ label: string; value: number }>,
    neighborhoodSeries: Array<{ label: string; value: number }>,
  ) => (
    <div style={{ marginBottom: 12 }}>
      <h3 style={{ marginBottom: 8 }}>{title}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <PieCardWithLegend subtitle="Metro Area" data={metroSeries} />
        <PieCardWithLegend subtitle="Neighborhood" data={neighborhoodSeries} />
      </div>
    </div>
  );

  return (
    <div>
      <h2>R - REAL ESTATE (Housing Market Fundamentals)</h2>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        Is the housing market healthy and investable?
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '14px' }}>
        <div className="card" style={{ padding: '20px' }}>
          <h3>Median Home Value</h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {medianHomeValue ? `$${Number(medianHomeValue).toLocaleString()}` : 'N/A'}
          </p>
        </div>
        <div className="card" style={{ padding: '20px' }}>
          <h3>Median Rent</h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {medianRent ? `$${Number(medianRent).toLocaleString()}/month` : 'N/A'}
          </p>
        </div>
        <div className="card" style={{ padding: '20px' }}>
          <h3>Price-to-Rent Ratio</h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {data.priceToRentRatio != null ? data.priceToRentRatio : 'N/A'}
          </p>
        </div>
        <div className="card" style={{ padding: '20px' }}>
          <h3>Affordability Index</h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {data.affordabilityIndex ?? 'N/A'}
          </p>
        </div>
        <div className="card" style={{ padding: '20px' }}>
          <h3>Market Health Score</h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {data.marketHealthScore ?? 'N/A'}
          </p>
        </div>
      </div>

      {renderBarCompare('Home types', metroHomeTypeData, neighborhoodHomeTypeData, 74)}
      {renderBarCompare('Number of bedrooms', metroBedroomData, neighborhoodBedroomData, 74)}
      {renderBarCompare('Housing rent', metroRentData, neighborhoodRentData, 74)}
      {renderBarCompare('Housing value', metroValueData, neighborhoodValueData, 74)}
      {renderBarCompare('Housing age', metroHousingAgeData, neighborhoodHousingAgeData, 74)}
      {renderPieCompare('Investor owned vs homeowner owned', metroInvestorData, neighborhoodInvestorData)}
      {renderPieCompare('Mortgaged vs clear', metroMortgageData, neighborhoodMortgageData)}

      {missingFields.length > 0 && (
        <RequiredApiNotice
          apis={['Rentcast AVM API', 'US Census ACS Housing API']}
          missingFields={missingFields}
        />
      )}
    </div>
  );
};

export default RealEstateDashboard;
